import React, { useCallback, useMemo } from 'react'
import { Box, Stack, styled } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectCurrentCart } from '../../../cart/storage/cart.slice'
import { CartDeliveryMode } from '../../../cart/storage/cart.types'
import { LifeCycleState } from '../../storage'
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage'
import { StoreModalOpenLink } from './StoreModalOpenLink'
import {
  StockAvailabilityCodes,
  StockAvailabilityStatus,
  StockAvailabilityStatusType,
} from './StockAvailabilityStatus'
import { ProductLifeCycleStatus, ProductLifeCycleStatusUI } from './ProductLifeCycleStatus'
import { selectProduct, selectProductAvailabilities } from '../../storage/stocks.slice'
import TestIds from '../../../../testIds'

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  [theme.breakpoints.between('sm', 'lg')]: {
    justifyContent: 'flex-start',
    gap: '24px',
  },
}))

interface StockAvailabilityProps {
  productId: number
  showStore?: boolean
  storeId?: string
}

export const StockAvailability = ({ productId, showStore, storeId }: StockAvailabilityProps) => {
  const stockAvailabilities = useSelector(selectProductAvailabilities(productId)) || []
  const product = useSelector(selectProduct(productId))

  const currentCart = useSelector(selectCurrentCart)
  const currentAccount = useSelector(selectCurrentFarmerAccount)
  const isTfo = currentAccount?.hasTfoAccess

  const availabilityForStore = useCallback(
    (inventoryLocationId: string) => {
      return stockAvailabilities.find(
        (stockAvailability) =>
          stockAvailability.inventLocationId === inventoryLocationId ||
          (storeId &&
            stockAvailability.inventLocationId.endsWith(storeId) &&
            stockAvailability.productId === productId),
      )
    },
    [stockAvailabilities, productId],
  )

  const hasStockInHomeBranch = useMemo(() => {
    const availability = stockAvailabilities.find(
      (stockAvailability) =>
        stockAvailability.inventLocationId === `265-${currentAccount?.homeBranchId}` &&
        stockAvailability.productId === productId,
    )
    if (availability) {
      return availability?.quantity > 0
    }
    return false
  }, [stockAvailabilities, currentAccount?.homeBranchId, productId])

  const hasStockInSelectedBranch = useMemo(() => {
    const availability = stockAvailabilities.find(
      (stockAvailability) =>
        stockAvailability.inventLocationId === currentCart?.pickupStore?.inventoryLocationId &&
        stockAvailability.productId === productId,
    )
    if (availability) {
      return availability?.quantity > 0
    }
    return false
  }, [stockAvailabilities, currentCart?.pickupStore, productId])

  const isDelivery = useMemo(() => {
    return currentCart?.cartDeliveryMode === CartDeliveryMode.DELIVERY
  }, [currentCart?.cartDeliveryMode])

  const stockAvailability = React.useMemo(() => {
    if (currentCart?.cartDeliveryMode === CartDeliveryMode.PICKUP) {
      return stockAvailabilities.find(
        (stockAvailability) =>
          stockAvailability.inventLocationId === currentCart?.pickupStore?.inventoryLocationId &&
          stockAvailability.productId === productId,
      )
    }

    // For Delivery
    const availability = stockAvailabilities.find(
      (stockAvailability) =>
        stockAvailability.inventLocationId === `265-${currentAccount?.homeBranchId}` &&
        stockAvailability.productId === productId,
    )

    return availability
  }, [
    currentAccount?.homeBranchId,
    currentCart?.cartDeliveryMode,
    currentCart?.pickupStore,
    stockAvailabilities,
    productId,
  ])

  const isBackOrderAllowed = React.useMemo(
    () => stockAvailability?.isBackOrderAllowed,
    [stockAvailability],
  )

  const renderShareholderStockAvailabilityStatus = React.useMemo(() => {
    if (product?.stockAvailability === 'Enquire') {
      return null
    }
    if (!currentCart?.cartDeliveryMode) {
      return null
    }
    if (isDelivery) {
      return null
    } else {
      // Pickup
      const cartQuantity =
        currentCart?.items.find((item) => item.productId === productId)?.quantity ?? 0
      if (stockAvailability) {
        if (stockAvailability?.quantity > 0 && stockAvailability?.quantity >= cartQuantity) {
          return (
            <StockAvailabilityStatus
              status={{
                code: StockAvailabilityCodes.IN_STOCK,
                label: 'In Stock',
                quantity: stockAvailability.quantity,
              }}
              isBackOrderAllowed={stockAvailability?.isBackOrderAllowed}
            />
          )
        }
      }
    }
    return null
  }, [
    currentCart?.items,
    currentCart?.cartDeliveryMode,
    isDelivery,
    product?.stockAvailability,
    productId,
    stockAvailability,
  ])

  const renderStockAvailabilityStatus = React.useMemo(() => {
    if (storeId) {
      const availability = availabilityForStore(storeId)

      if (availability) {
        if (
          product?.lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE &&
          availability.quantity <= 0
        ) {
          return null
        } else {
          return (
            <StockAvailabilityStatus
              status={{
                code: availability.inventoryLevelCode as StockAvailabilityCodes,
                label: availability.inventoryLevelLabel,
                quantity: availability.quantity,
              }}
              isBackOrderAllowed={availability?.isBackOrderAllowed}
            />
          )
        }
      } else {
        return null
      }
    }

    const badge = (
      <StockAvailabilityStatus
        status={
          {
            code: stockAvailability?.inventoryLevelCode,
            label: stockAvailability?.inventoryLevelLabel,
            quantity: stockAvailability?.quantity,
          } as StockAvailabilityStatusType
        }
        isBackOrderAllowed={isBackOrderAllowed}
      />
    )

    if (isTfo) {
      if (
        product?.lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE &&
        stockAvailability &&
        stockAvailability?.quantity <= 0
      ) {
        return null
      } else {
        return badge
      }
    } else {
      return renderShareholderStockAvailabilityStatus
    }
  }, [
    availabilityForStore,
    isBackOrderAllowed,
    isTfo,
    renderShareholderStockAvailabilityStatus,
    stockAvailability,
    storeId,
  ])

  const renderShareHolderPickupProductLifeCycleStatus = useMemo(() => {
    const cartQuantity =
      currentCart?.items.find((item) => item.productId === productId)?.quantity ?? 0
    if (stockAvailability) {
      if (stockAvailability?.quantity > 0 && stockAvailability?.quantity >= cartQuantity) {
        return null
      } else {
        return (
          <ProductLifeCycleStatusUI
            label='Not available for pick up'
            subtitle='We will contact you for fulfilment options'
          />
        )
      }
    }
  }, [currentCart?.items, stockAvailability, productId, product?.lifeCycleState])

  const renderShareHoldersUnavailableProductLifeCycleStatus = useMemo(() => {
    if (stockAvailability) {
      if (product?.lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE) {
        return (
          <ProductLifeCycleStatusUI
            label='Temporarily unavailable'
            subtitle='We will contact you for fulfilment options'
          />
        )
      }
      if (stockAvailability?.quantity <= 0) {
        return <ProductLifeCycleStatusUI label='Urgent delivery not available' />
      } else {
        return (
          <ProductLifeCycleStatusUI subtitle="Full quantity can't be delivered urgently, we'll contact you for fulfilment options" />
        )
      }
    }
  }, [stockAvailability, product?.lifeCycleState])

  const renderShareHoldersProductLifeCycleStatus = useMemo(() => {
    if (storeId) return null
    const cartQuantity =
      currentCart?.items.find((item) => item.productId === productId)?.quantity ?? 0
    if (isDelivery) {
      if (!currentCart?.deliveryDetails?.urgent) {
        return null
      }
      if (stockAvailability) {
        if (stockAvailability?.quantity > 0 && stockAvailability?.quantity >= cartQuantity) {
          return null
        } else {
          return renderShareHoldersUnavailableProductLifeCycleStatus
        }
      } else {
        return null
      }
    } else {
      return renderShareHolderPickupProductLifeCycleStatus
    }
  }, [
    currentCart?.items,
    currentCart?.deliveryDetails?.urgent,
    isDelivery,
    productId,
    storeId,
    stockAvailability,
    renderShareHoldersUnavailableProductLifeCycleStatus,
    renderShareHolderPickupProductLifeCycleStatus,
  ])

  const renderTFOProductLifeCycleStatus = React.useMemo(() => {
    if (!product) {
      return null
    }
    if (product.lifeCycleState === LifeCycleState.STOCKED) {
      return null
    }

    const badge = (
      <ProductLifeCycleStatus
        lifeCycleState={product?.lifeCycleState}
        basePriceExcGst={product?.basePriceExcGst}
      />
    )

    if (storeId) {
      const availability = availabilityForStore(storeId)
      if (availability && availability?.quantity > 0) {
        return null
      } else {
        return badge
      }
    }
    if (isDelivery) {
      if (!hasStockInHomeBranch) {
        return badge
      } else {
        return null
      }
    } else if (!hasStockInSelectedBranch) {
      return badge
    } else {
      return null
    }
  }, [
    availabilityForStore,
    hasStockInHomeBranch,
    hasStockInSelectedBranch,
    isDelivery,
    product,
    storeId,
  ])

  const renderProductLifeCycleStatus = React.useMemo(() => {
    if (!product) {
      return null
    }
    if (isTfo) {
      return renderTFOProductLifeCycleStatus
    } else {
      // Shareholders
      if (product.stockAvailability === 'Enquire') {
        return null
      }

      return renderShareHoldersProductLifeCycleStatus
    }
  }, [product, isTfo, renderTFOProductLifeCycleStatus, renderShareHoldersProductLifeCycleStatus])

  const storeModalLinkName = useMemo(() => {
    return currentCart?.cartDeliveryMode === CartDeliveryMode.PICKUP
      ? currentCart?.pickupStore?.orgUnitName ?? 'Check availability'
      : 'Check availability'
  }, [currentCart?.pickupStore?.orgUnitName, currentCart?.cartDeliveryMode])

  if (!showStore && !stockAvailability) {
    return null
  }

  return (
    <Container>
      {renderStockAvailabilityStatus || renderProductLifeCycleStatus ? (
        <Stack spacing={1} data-testid={TestIds.feat.stock.status}>
          {renderStockAvailabilityStatus}
          {renderProductLifeCycleStatus}
        </Stack>
      ) : null}
      {showStore && <StoreModalOpenLink name={storeModalLinkName} productId={productId} />}
    </Container>
  )
}
